import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FaEye,
  FaBan,
  FaMapMarkerAlt,
  FaTimes,
  FaInfoCircle,
  FaUnlock
} from "react-icons/fa";
import { block_ad, get_all_ads } from "../../store/services/authentication";

const Ads = () => {
  const dispatch = useDispatch();
  const { get_all_ads_data, block_ad_status } = useSelector((state) => state.userAuth);
  const [selectedAd, setSelectedAd] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [selectedAdId, setSelectedAdId] = useState(null);

  useEffect(() => {
    dispatch(get_all_ads());
  }, [dispatch, block_ad_status]);

  const handleBlock = (adId, isCurrentlyBlocked) => {
    if (isCurrentlyBlocked) {
      if (window.confirm("Are you sure you want to unblock this ad?")) {
        dispatch(block_ad({ id: adId, is_block: false, reason: '' }));
      }
    } else {
      setSelectedAdId(adId);
      setBlockReason("");
      document.getElementById("block_modal").showModal();
    }
  };

  const confirmBlock = () => {
    if (blockReason.trim()) {
      dispatch(block_ad({ id: selectedAdId, is_block: true, reason: blockReason }));
      document.getElementById("block_modal").close();
    }
  };

  const openAdDetails = (ad) => {
    setSelectedAd(ad);
    setIsModalOpen(true);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Ads Management</h1>

      {/* Block Reason Modal */}
      <dialog id="block_modal" className="modal">
        <div className="modal-box bg-white">
          <h3 className="font-bold text-lg text-gray-800">Block Ad Reason</h3>
          <p className="py-4 text-gray-600">Please provide a reason for blocking this ad:</p>
          <textarea
            className="textarea textarea-bordered w-full mb-4 bg-gray-50 text-black"
            placeholder="Enter block reason..."
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
          />
          <div className="modal-action">
            <form method="dialog" className="flex gap-2">
              <button className="btn btn-ghost text-black hover:text-white">Cancel</button>
              <button 
                className="btn bg-red-600 text-white hover:bg-red-700"
                onClick={confirmBlock}
              >
                Confirm Block
              </button>
            </form>
          </div>
        </div>
      </dialog>

      {/* Ads Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {get_all_ads_data?.ads?.map((ad) => (
          <div
            key={ad._id}
            className={`relative bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-200 hover:shadow-xl ${
              ad.is_block ? "border-2 border-red-500" : "border border-gray-200"
            }`}
          >
            {/* Blocked Banner */}
            {ad.is_block && (
              <div className="absolute top-0 left-0 bg-red-500 text-white px-4 py-1 text-sm font-medium flex items-center">
                <span>Blocked</span>
                {ad.block_reason && (
                  <div className="tooltip" data-tip={ad.block_reason}>
                    <FaInfoCircle className="ml-2" />
                  </div>
                )}
              </div>
            )}

            {/* Ad Image */}
            <img
              src={ad.images[0]}
              alt={ad.title}
              className="w-full h-48 object-cover"
            />

            {/* Ad Content */}
            <div className="p-4">
              <h3 className="text-lg font-semibold mb-2 text-gray-800">{ad.title}</h3>
              <p className="text-gray-600 text-sm mb-2 line-clamp-2">
                {ad.description}
              </p>

              <div className="flex items-center text-sm text-gray-500 mb-2">
                <FaMapMarkerAlt className="mr-2" />
                <span className="truncate">{ad.location_url}</span>
              </div>

              <div className="flex justify-between items-center text-sm">
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                  {ad.category?.name}
                </span>
                <span
                  className={`px-2 py-1 rounded-full text-xs ${
                    ad.status === "Active"
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {ad.status}
                </span>
              </div>
            </div>

            {/* Admin Actions */}
            <div className="border-t border-gray-200 p-4 flex justify-end gap-2">
              <button
                onClick={() => openAdDetails(ad)}
                className="text-blue-600 hover:text-blue-800 transition-colors"
              >
                <FaEye className="text-lg" />
              </button>
              <button
                onClick={() => handleBlock(ad._id, ad.is_block)}
                className={`transition-colors ${
                  ad.is_block
                    ? "text-green-600 hover:text-green-800"
                    : "text-yellow-600 hover:text-yellow-800"
                }`}
              >
                {ad.is_block ? <FaUnlock className="text-lg" /> : <FaBan className="text-lg" />}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Ad Details Modal */}
      {isModalOpen && selectedAd && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-gray-800">{selectedAd.title}</h2>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors"
              >
                <FaTimes className="h-6 w-6" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <img
                  src={selectedAd.images[0]}
                  alt={selectedAd.title}
                  className="w-full h-64 object-cover rounded-lg"
                />
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2 text-gray-800">Description</h3>
                  <p className="text-gray-600">{selectedAd.description}</p>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                {/* Statistics Cards */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-600">Total Clicks</p>
                    <p className="text-2xl font-bold text-blue-800">
                      {selectedAd.clicks?.length || 0}
                    </p>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-green-600">Conversions</p>
                    <p className="text-2xl font-bold text-green-800">
                      {selectedAd.conversions?.length || 0}
                    </p>
                  </div>
                </div>

                {/* Location Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-2 text-gray-800">Location</h3>
                  <p className="text-gray-600">{selectedAd.location_url}</p>
                  {/* <div className="mt-4 aspect-video bg-gray-200 rounded-lg">
                     Map integration placeholder 
                  </div> */}
                </div>

                {/* Details Section */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold mb-3 text-gray-800">Details</h3>
                  <dl className="grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm text-gray-500">Category</dt>
                      <dd className="text-sm font-medium text-gray-800">
                        {selectedAd.category?.name}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Status</dt>
                      <dd className="text-sm font-medium text-gray-800">
                        {selectedAd.status}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Created At</dt>
                      <dd className="text-sm font-medium text-gray-800">
                        {new Date(selectedAd.createdAt).toLocaleDateString()}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm text-gray-500">Last Updated</dt>
                      <dd className="text-sm font-medium text-gray-800">
                        {new Date(selectedAd.updatedAt).toLocaleDateString()}
                      </dd>
                    </div>
                  </dl>
                </div>


                {/* Block Reason Section */}
                {selectedAd.is_block && selectedAd.reason && (
                  <div className="bg-red-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-semibold text-red-800">Block Reason</h3>
                      {/* <button
                        onClick={() => handleBlock(selectedAd._id, true)}
                        className="text-green-600 hover:text-green-800 flex items-center gap-1"
                      >
                        <FaUnlock className="inline-block" />
                        <span>Unblock</span>
                      </button> */}
                    </div>
                    <p className="text-red-700">{selectedAd.reason}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!get_all_ads_data?.ads?.length && (
        <div className="text-center py-8 text-gray-500">No ads found</div>
      )}
    </div>
  );
};

export default Ads;



// import React, { useEffect, useState } from "react";
// import { useDispatch, useSelector } from "react-redux";
// import {
//   FaEye,
//   FaBan,
//   FaMapMarkerAlt,
//   FaTimes,
//   FaInfoCircle,
//   FaUnlock,
//   FaPlus,
//   FaEdit,
//   FaTrash
// } from "react-icons/fa";
// import { 
//   block_ad, 
//   get_all_ads,
//   // create_ad,
//   // update_ad,
//   // delete_ad
// } from "../../store/services/authentication";
// import { asyncStatus } from "../../utils/contants/asyncStatus";

// const Ads = () => {
//   const dispatch = useDispatch();
//   const { 
//     get_all_ads_data, 
//     block_ad_status,
//     create_ad_status,
//     update_ad_status,
//     delete_ad_status,
//     error
//   } = useSelector((state) => state.userAuth);

//   const [selectedAd, setSelectedAd] = useState(null);
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [isFormModalOpen, setIsFormModalOpen] = useState(false);
//   const [isEditMode, setIsEditMode] = useState(false);
//   const [blockReason, setBlockReason] = useState("");
//   const [selectedAdId, setSelectedAdId] = useState(null);
//   const [formData, setFormData] = useState({
//     title: '',
//     description: '',
//     category: '',
//     location_url: '',
//     status: 'Active'
//   });
//   const [formError, setFormError] = useState(null);

//   useEffect(() => {
//     dispatch(get_all_ads());
//   }, [dispatch, block_ad_status, create_ad_status, update_ad_status, delete_ad_status]);

//   useEffect(() => {
//     if (create_ad_status === asyncStatus.SUCCEEDED || 
//         update_ad_status === asyncStatus.SUCCEEDED) {
//       setIsFormModalOpen(false);
//       setFormError(null);
//     }
//   }, [create_ad_status, update_ad_status]);

//   const handleInputChange = (e) => {
//     setFormData({
//       ...formData,
//       [e.target.name]: e.target.value
//     });
//   };

//   const handleCreateAd = () => {
//     setIsFormModalOpen(true);
//     setIsEditMode(false);
//     setFormData({
//       title: '',
//       description: '',
//       category: '',
//       location_url: '',
//       status: 'Active'
//     });
//   };

//   const handleEditAd = (ad) => {
//     setSelectedAd(ad);
//     setIsEditMode(true);
//     setIsFormModalOpen(true);
//     setFormData({
//       title: ad.title,
//       description: ad.description,
//       category: ad.category?._id,
//       location_url: ad.location_url,
//       status: ad.status
//     });
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setFormError(null);
    
//     try {
//       if(isEditMode) {
//         // await dispatch(update_ad({ 
//         //   id: selectedAd._id, 
//         //   ...formData 
//         // })).unwrap();
//       } else {
//         // await dispatch(create_ad(formData)).unwrap();
//       }
//     } catch (error) {
//       setFormError(error.message || "An error occurred");
//     }
//   };

//   const handleDelete = async (adId) => {
//     if (window.confirm("Are you sure you want to delete this ad?")) {
//       try {
//         // await dispatch(delete_ad(adId)).unwrap();
//       } catch (error) {
//         alert(`Delete failed: ${error.message}`);
//       }
//     }
//   };

//   const handleBlock = (adId, isCurrentlyBlocked) => {
//     if (isCurrentlyBlocked) {
//       if (window.confirm("Are you sure you want to unblock this ad?")) {
//         dispatch(block_ad({ id: adId, is_block: false, reason: '' }));
//       }
//     } else {
//       setSelectedAdId(adId);
//       setBlockReason("");
//       document.getElementById("block_modal").showModal();
//     }
//   };

//   const confirmBlock = () => {
//     if (blockReason.trim()) {
//       dispatch(block_ad({ id: selectedAdId, is_block: true, reason: blockReason }));
//       document.getElementById("block_modal").close();
//     }
//   };

//   const openAdDetails = (ad) => {
//     setSelectedAd(ad);
//     setIsModalOpen(true);
//   };

//   return (
//     <div className="container mx-auto px-4 py-8">
//       <div className="flex justify-between items-center mb-6">
//         <h1 className="text-2xl font-bold text-gray-800">Ads Management</h1>
//         <button
//           onClick={handleCreateAd}
//           className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
//         >
//           <FaPlus className="inline-block" />
//           Create Ad
//         </button>
//       </div>

//       {/* Add/Edit Ad Modal */}
//       {isFormModalOpen && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
//             <div className="flex justify-between items-center mb-4">
//               <h2 className="text-2xl font-semibold text-gray-800">
//                 {isEditMode ? 'Edit Ad' : 'Create New Ad'}
//               </h2>
//               <button
//                 onClick={() => {
//                   setIsFormModalOpen(false);
//                   setFormError(null);
//                 }}
//                 className="text-gray-500 hover:text-gray-700 transition-colors"
//               >
//                 <FaTimes className="h-6 w-6" />
//               </button>
//             </div>

//             {(formError || error) && (
//               <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
//                 {formError || error?.message}
//               </div>
//             )}

//             <form onSubmit={handleSubmit} className="space-y-4">
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Title *
//                   </label>
//                   <input
//                     type="text"
//                     name="title"
//                     value={formData.title}
//                     onChange={handleInputChange}
//                     className="w-full px-3 py-2 border rounded-lg"
//                     required
//                   />
//                 </div>

//                 <div>
//                   <label className="block text-sm font-medium text-gray-700 mb-1">
//                     Category *
//                   </label>
//                   <select
//                     name="category"
//                     value={formData.category}
//                     onChange={handleInputChange}
//                     className="w-full px-3 py-2 border rounded-lg"
//                     required
//                   >
//                     <option value="">Select Category</option>
//                     {/* Populate categories from your API */}
//                     <option value="1">Category 1</option>
//                     <option value="2">Category 2</option>
//                   </select>
//                 </div>
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Description *
//                 </label>
//                 <textarea
//                   name="description"
//                   value={formData.description}
//                   onChange={handleInputChange}
//                   className="w-full px-3 py-2 border rounded-lg h-32"
//                   required
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Location URL *
//                 </label>
//                 <input
//                   type="url"
//                   name="location_url"
//                   value={formData.location_url}
//                   onChange={handleInputChange}
//                   className="w-full px-3 py-2 border rounded-lg"
//                   required
//                 />
//               </div>

//               <div>
//                 <label className="block text-sm font-medium text-gray-700 mb-1">
//                   Status *
//                 </label>
//                 <select
//                   name="status"
//                   value={formData.status}
//                   onChange={handleInputChange}
//                   className="w-full px-3 py-2 border rounded-lg"
//                 >
//                   <option value="Active">Active</option>
//                   <option value="Inactive">Inactive</option>
//                 </select>
//               </div>

//               <div className="mt-6">
//                 <button
//                   type="submit"
//                   className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700"
//                 >
//                   {isEditMode ? 'Update Ad' : 'Create Ad'}
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}

//       {/* Block Reason Modal */}
//       <dialog id="block_modal" className="modal">
//         <div className="modal-box bg-white">
//           <h3 className="font-bold text-lg text-gray-800">Block Ad Reason</h3>
//           <p className="py-4 text-gray-600">Please provide a reason for blocking this ad:</p>
//           <textarea
//             className="textarea textarea-bordered w-full mb-4 bg-gray-50 text-black"
//             placeholder="Enter block reason..."
//             value={blockReason}
//             onChange={(e) => setBlockReason(e.target.value)}
//           />
//           <div className="modal-action">
//             <form method="dialog" className="flex gap-2">
//               <button className="btn btn-ghost text-black hover:text-white">Cancel</button>
//               <button 
//                 className="btn bg-red-600 text-white hover:bg-red-700"
//                 onClick={confirmBlock}
//               >
//                 Confirm Block
//               </button>
//             </form>
//           </div>
//         </div>
//       </dialog>

//       {/* Ads Grid */}
//       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
//         {get_all_ads_data?.ads?.map((ad) => (
//           <div
//             key={ad._id}
//             className={`relative bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-200 hover:shadow-xl ${
//               ad.is_block ? "border-2 border-red-500" : "border border-gray-200"
//             }`}
//           >
//             {ad.is_block && (
//               <div className="absolute top-0 left-0 bg-red-500 text-white px-4 py-1 text-sm font-medium flex items-center">
//                 <span>Blocked</span>
//                 {ad.block_reason && (
//                   <div className="tooltip" data-tip={ad.block_reason}>
//                     <FaInfoCircle className="ml-2" />
//                   </div>
//                 )}
//               </div>
//             )}

//             <img
//               src={ad.images[0]}
//               alt={ad.title}
//               className="w-full h-48 object-cover"
//             />

//             <div className="p-4">
//               <h3 className="text-lg font-semibold mb-2 text-gray-800">{ad.title}</h3>
//               <p className="text-gray-600 text-sm mb-2 line-clamp-2">
//                 {ad.description}
//               </p>

//               <div className="flex items-center text-sm text-gray-500 mb-2">
//                 <FaMapMarkerAlt className="mr-2" />
//                 <span className="truncate">{ad.location_url}</span>
//               </div>

//               <div className="flex justify-between items-center text-sm">
//                 <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
//                   {ad.category?.name}
//                 </span>
//                 <span
//                   className={`px-2 py-1 rounded-full text-xs ${
//                     ad.status === "Active"
//                       ? "bg-green-100 text-green-800"
//                       : "bg-gray-100 text-gray-600"
//                   }`}
//                 >
//                   {ad.status}
//                 </span>
//               </div>
//             </div>

//             <div className="border-t border-gray-200 p-4 flex justify-end gap-2">
//               <button
//                 onClick={() => openAdDetails(ad)}
//                 className="text-blue-600 hover:text-blue-800 transition-colors"
//               >
//                 <FaEye className="text-lg" />
//               </button>
//               <button
//                 onClick={() => handleEditAd(ad)}
//                 className="text-yellow-600 hover:text-yellow-800 transition-colors"
//               >
//                 <FaEdit className="text-lg" />
//               </button>
//               <button
//                 onClick={() => handleDelete(ad._id)}
//                 className="text-red-600 hover:text-red-800 transition-colors"
//               >
//                 <FaTrash className="text-lg" />
//               </button>
//               <button
//                 onClick={() => handleBlock(ad._id, ad.is_block)}
//                 className={`transition-colors ${
//                   ad.is_block
//                     ? "text-green-600 hover:text-green-800"
//                     : "text-yellow-600 hover:text-yellow-800"
//                 }`}
//               >
//                 {ad.is_block ? <FaUnlock className="text-lg" /> : <FaBan className="text-lg" />}
//               </button>
//             </div>
//           </div>
//         ))}
//       </div>

//       {/* Ad Details Modal */}
//       {isModalOpen && selectedAd && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
//             <div className="flex justify-between items-center mb-4">
//               <h2 className="text-2xl font-semibold text-gray-800">{selectedAd.title}</h2>
//               <button
//                 onClick={() => setIsModalOpen(false)}
//                 className="text-gray-500 hover:text-gray-700 transition-colors"
//               >
//                 <FaTimes className="h-6 w-6" />
//               </button>
//             </div>

//             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//               <div className="space-y-4">
//                 <img
//                   src={selectedAd.images[0]}
//                   alt={selectedAd.title}
//                   className="w-full h-64 object-cover rounded-lg"
//                 />
                
//                 <div className="bg-gray-50 p-4 rounded-lg">
//                   <h3 className="font-semibold mb-2 text-gray-800">Description</h3>
//                   <p className="text-gray-600">{selectedAd.description}</p>
//                 </div>
//               </div>

//               <div className="space-y-4">
//                 <div className="grid grid-cols-2 gap-4">
//                   <div className="bg-blue-50 p-4 rounded-lg">
//                     <p className="text-sm text-blue-600">Total Clicks</p>
//                     <p className="text-2xl font-bold text-blue-800">
//                       {selectedAd.clicks?.length || 0}
//                     </p>
//                   </div>
//                   <div className="bg-green-50 p-4 rounded-lg">
//                     <p className="text-sm text-green-600">Conversions</p>
//                     <p className="text-2xl font-bold text-green-800">
//                       {selectedAd.conversions?.length || 0}
//                     </p>
//                   </div>
//                 </div>

//                 <div className="bg-gray-50 p-4 rounded-lg">
//                   <h3 className="font-semibold mb-2 text-gray-800">Location</h3>
//                   <p className="text-gray-600">{selectedAd.location_url}</p>
//                 </div>

//                 <div className="bg-gray-50 p-4 rounded-lg">
//                   <h3 className="font-semibold mb-3 text-gray-800">Details</h3>
//                   <dl className="grid grid-cols-2 gap-4">
//                     <div>
//                       <dt className="text-sm text-gray-500">Category</dt>
//                       <dd className="text-sm font-medium text-gray-800">
//                         {selectedAd.category?.name}
//                       </dd>
//                     </div>
//                     <div>
//                       <dt className="text-sm text-gray-500">Status</dt>
//                       <dd className="text-sm font-medium text-gray-800">
//                         {selectedAd.status}
//                       </dd>
//                     </div>
//                     <div>
//                       <dt className="text-sm text-gray-500">Created At</dt>
//                       <dd className="text-sm font-medium text-gray-800">
//                         {new Date(selectedAd.createdAt).toLocaleDateString()}
//                       </dd>
//                     </div>
//                     <div>
//                       <dt className="text-sm text-gray-500">Last Updated</dt>
//                       <dd className="text-sm font-medium text-gray-800">
//                         {new Date(selectedAd.updatedAt).toLocaleDateString()}
//                       </dd>
//                     </div>
//                   </dl>
//                 </div>

//                 {selectedAd.is_block && selectedAd.reason && (
//                   <div className="bg-red-50 p-4 rounded-lg">
//                     <div className="flex justify-between items-center mb-2">
//                       <h3 className="font-semibold text-red-800">Block Reason</h3>
//                     </div>
//                     <p className="text-red-700">{selectedAd.reason}</p>
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {!get_all_ads_data?.ads?.length && (
//         <div className="text-center py-8 text-gray-500">No ads found</div>
//       )}
//     </div>
//   );
// };

// export default Ads;